import React, { useEffect, useMemo, useState } from 'react';
import { entityLabel } from './entityLabels';
import { dataQualityService } from '../../services/dataQualityService';
import { entityTotals } from '../../lib/dataState';
import DataQualityStatusBadge from '../DataQualityStatusBadge';
import { fieldRows, segmentTotals, uplift, change, healthOf, pctOf, completeness } from '../../lib/composition';

// The question this view answers: how complete was the data you gave us, and how much
// did SignalCue add on top?
//
// Every bar is 100% of a field's rows, in four provenance bands: what your ATS
// carried, what SignalCue added, what is still missing (fillable), and what we
// searched but genuinely can't enrich (evidence-backed dead-ends). That last band is
// its OWN light-red segment — not folded into the grey gap — so a reader sees the real
// ceiling at a glance.
const PROV = {
  source: { c: '#eda100', ink: '#26251f', label: 'From your ATS', hint: 'the export already carried this value' },
  added: { c: '#1baf7a', ink: '#fff', label: 'Added by SignalCue', hint: 'we derived it from your other fields, or discovered it externally' },
  missing: { c: '#d6d5cf', ink: '#26251f', label: 'Missing', hint: 'still empty — can be filled, or not tried yet' },
  exhausted: { c: '#e79797', ink: '#6e2020', label: "Can't enrich", hint: 'searched, nothing verifiable found' },
  source_owned: { c: '#9fb3c8', ink: '#22303f', label: 'Not enriched', hint: "SignalCue doesn't enrich this — it's foundational identity or sensitive data that comes from your ATS" }
};

// The bar's drawn bands, in order. Distinct from composition's SEGMENTS
// (source/added/missing, used for totals): here the grey "missing" band is the
// fillable remainder, and the red "exhausted" (can't-enrich) AND blue "source_owned"
// (we don't enrich this — from your ATS) bands are carved OUT of it, so all still
// re-sum to the original gap and the bands total the row count.
const BAR_SEGMENTS = ['source', 'added', 'missing', 'exhausted', 'source_owned'];
function barValue(totals, seg) {
  if (seg === 'missing') return Math.max(0, (totals.missing || 0) - (totals.exhausted || 0) - (totals.source_owned || 0));
  return totals[seg] || 0;
}

const fmt = (n) => Number(n || 0).toLocaleString('en-US');
const pct = (n) => `${Number(n || 0).toFixed(0)}%`;

const FIELD_LABEL = (name) => String(name || '').replace(/_/g, ' ');

// multiplier: a field the ATS never had is "new", not ×∞; a field we never touched is
// ×1 and must not read as a win.
function multiplier({ factor, isNew, after }) {
  if (isNew) return { text: 'new', tone: 'text-emerald-700 font-semibold' };
  if (!after) return { text: '—', tone: 'text-gray-300' };
  if (factor > 1.05) return { text: `×${factor.toFixed(1)}`, tone: 'text-emerald-700 font-semibold' };
  return { text: '×1.0', tone: 'text-gray-400' };
}

// segmentTitle spells out what a segment holds, including the parts we folded into it.
function segmentTitle(seg, val, whole, totals) {
  const p = pctOf(val, whole).toFixed(1);
  const base = `${PROV[seg].label} — ${fmt(val)} of ${fmt(whole)} (${p}%)`;
  if (seg === 'added' && (totals.derived || totals.enriched)) {
    return `${base}: ${fmt(totals.derived)} computed from your own fields, ${fmt(totals.enriched)} found externally.`;
  }
  if (seg === 'missing' && totals.actionable) {
    return `${base}. ${fmt(totals.actionable)} of these can still be filled by enrichment.`;
  }
  return `${base}. ${PROV[seg].hint}`;
}

// segmentLines — the detail rows a segment's hover tooltip shows. It spells out the
// parts folded INTO a segment: the "added" split (derived vs external), and — the
// design keeps these in the tooltip rather than as their own bar segments — the
// "missing" split into still-fillable vs tried-and-can't-enrich.
function segmentLines(seg, val, whole, totals) {
  const p = pctOf(val, whole).toFixed(1);
  const lines = [`${fmt(val)} of ${fmt(whole)} values (${p}%)`];
  if (seg === 'added' && (totals.derived || totals.enriched)) {
    if (totals.derived) lines.push(`${fmt(totals.derived)} derived from your own fields`);
    if (totals.enriched) lines.push(`${fmt(totals.enriched)} found externally`);
  } else if (seg === 'missing') {
    lines.push(totals.actionable ? `${fmt(totals.actionable)} can still be filled` : PROV.missing.hint);
  } else {
    lines.push(PROV[seg].hint); // source, exhausted
  }
  return lines;
}

// CompositionBar — source | added | missing, to scale. Hovering (or focusing) a
// segment opens a styled tooltip detailing that section — instant and readable,
// unlike the browser's delayed native title. Kept as an aria-label for a11y too.
function CompositionBar({ totals, height = 22, showCounts = true, minLabelPct = 9 }) {
  const whole = BAR_SEGMENTS.reduce((n, s) => n + barValue(totals, s), 0);
  const [hover, setHover] = useState(null);
  const clear = (seg) => setHover((h) => (h === seg ? null : h));
  return (
    <div className="relative">
      <div className="flex gap-0.5 rounded overflow-hidden" style={{ height, background: PROV.missing.c }}>
        {BAR_SEGMENTS.map((seg) => {
          const val = barValue(totals, seg);
          if (val <= 0) return null;
          const p = pctOf(val, whole);
          return (
            <div
              key={seg}
              data-testid={`bar-seg-${seg}`}
              tabIndex={0}
              aria-label={segmentTitle(seg, val, whole, totals)}
              className="h-full flex items-center justify-center min-w-0 cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
              style={{ flex: `0 0 max(${p}%, 3px)`, background: PROV[seg].c }}
              onMouseEnter={() => setHover(seg)}
              onMouseLeave={() => clear(seg)}
              onFocus={() => setHover(seg)}
              onBlur={() => clear(seg)}
            >
              {showCounts && p > minLabelPct && (
                <span className="text-[11px] font-semibold whitespace-nowrap tabular-nums" style={{ color: PROV[seg].ink }}>
                  {fmt(val)}
                </span>
              )}
            </div>
          );
        })}
      </div>
      {hover && (
        <div
          role="tooltip"
          className="absolute bottom-full left-0 mb-1.5 z-20 w-max max-w-[16rem] rounded-lg bg-gray-900 px-3 py-2 text-[11px] leading-relaxed shadow-lg pointer-events-none"
        >
          <div className="font-semibold" style={{ color: PROV[hover].c }}>
            {PROV[hover].label}
          </div>
          {segmentLines(hover, barValue(totals, hover), whole, totals).map((line, i) => (
            <div key={i} className="text-gray-200 tabular-nums">
              {line}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Card({ label, value, hint, loading, accent }) {
  return (
    <div
      className="bg-white rounded-xl shadow-sm border border-gray-100 p-5"
      style={accent ? { boxShadow: `inset 3px 0 0 ${accent}` } : undefined}
    >
      <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
      {loading ? (
        <div className="h-8 w-24 bg-gray-100 rounded mt-2 animate-pulse motion-reduce:animate-none" />
      ) : (
        <p className="text-2xl font-semibold text-gray-900 mt-2 tabular-nums">{value}</p>
      )}
      {hint ? <p className="text-xs text-gray-400 mt-1">{hint}</p> : null}
    </div>
  );
}

function Chevron({ open }) {
  return (
    <svg
      viewBox="0 0 20 20"
      className={`w-4 h-4 flex-none text-gray-400 transition-transform motion-reduce:transition-none ${open ? 'rotate-90' : ''}`}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path d="M7 4l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// The shell that binds an entity's expanded field list to the entity above it: a
// left rail dropping from ~under the chevron (ml-3 ≈ chevron centre) plus a faint
// tint, so the group reads as a contained SUB-panel of its parent rather than a
// flat sibling list. Per-state padding is appended by each caller.
export const NEST_SHELL = 'ml-3 mr-1 mb-3 border-l-2 border-gray-300 bg-gray-50/70 rounded-r-lg';

// FieldRow — one bar, one trend, one verdict. A header line (name, before → after,
// change badge, View data) sits above a full-width bar. Header controls wrap on a
// narrow screen; the bar always has the full row width, so its segment labels never
// get crushed the way a fixed-column grid crushed them.
function FieldRow({ row, onViewData, tableName }) {
  const u = uplift(row);
  const m = multiplier(u);
  const coverage = pctOf(row.filled, row.total);

  return (
    <li className="py-2 border-b border-gray-50 last:border-0 flex flex-wrap items-center gap-x-4 gap-y-1 xl:flex-nowrap">
      <span className="order-1 min-w-0 flex-1 basis-40 xl:flex-none xl:basis-44">
        <span className="text-sm text-gray-800" title={FIELD_LABEL(row.field)}>
          {FIELD_LABEL(row.field)}
        </span>
        <span className="text-[11px] text-gray-400 tabular-nums ml-2">{pct(coverage)} complete</span>
        {/* Split this field's gap: the records we searched and can't currently fill vs
            those still worth an enrich run. Same wording source as the bar's red band. */}
        {row.exhausted > 0 && (
          <span
            className="block text-[11px] text-gray-400 tabular-nums mt-0.5"
            title={`Searched — ${PROV.exhausted.hint}`}
          >
            {`${fmt(row.exhausted)} can't enrich${row.actionable > 0 ? ` · ${fmt(row.actionable)} to try` : ''}`}
          </span>
        )}
      </span>

      <span className="order-last basis-full min-w-0 xl:order-2 xl:basis-0 xl:flex-1">
        <CompositionBar totals={row} height={14} minLabelPct={11} />
      </span>

      <span className="order-2 xl:order-3 flex items-center gap-3 ml-auto xl:ml-0 flex-none">
        <span className="text-xs tabular-nums text-gray-600 whitespace-nowrap">
          {fmt(u.before)} <span className="text-gray-300">→</span> {fmt(u.after)}
          <span className={`ml-1.5 ${m.tone}`}>{m.text}</span>
        </span>
        <DataQualityStatusBadge status={change(row)} kind="improvement" />
        <button
          type="button"
          onClick={() => onViewData(tableName, row.field)}
          className="text-xs text-[#007A6B] hover:underline whitespace-nowrap"
        >
          View data
        </button>
      </span>
    </li>
  );
}

function FieldRows({ rows, loading, onViewData, tableName, recordCount }) {
  if (loading) {
    return (
      <div className={`${NEST_SHELL} pl-4 pr-2 py-3 space-y-2`}>
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-7 bg-gray-100 rounded animate-pulse motion-reduce:animate-none" />
        ))}
      </div>
    );
  }
  if (!rows?.length) {
    return (
      <div className={`${NEST_SHELL} pl-4 pr-2 py-3`}>
        <p className="text-sm text-gray-500">No fields profiled for this record type yet.</p>
      </div>
    );
  }
  // Biggest contribution first — the whole point of the panel.
  const sorted = [...rows].sort((a, b) => b.added - a.added || b.filled - a.filled);
  return (
    // Child field rows, held inside the nesting shell so they read as belonging to
    // the entity above. Content clears the rail (pl-4) and lands just right of the
    // parent's label.
    <div className={`${NEST_SHELL} pl-4 pr-2 pt-2 pb-1`}>
      <p className="text-[11px] text-gray-400 pb-1.5">
        Each bar covers all {fmt(recordCount)} records: what your ATS carried, what we added, what is still
        missing — then the before → after count.
      </p>
      <ul>
        {sorted.map((r) => (
          <FieldRow key={r.field} row={r} onViewData={onViewData} tableName={tableName} />
        ))}
      </ul>
    </div>
  );
}

function EntityRow({ table, rows, detailLoading, onViewData }) {
  const [open, setOpen] = useState(false);
  // Backend flag (coverage.IsEnrichableEntity). Undefined on older payloads, which
  // must keep the enrichable presentation rather than silently losing their badge.
  const enrichable = table.enrichable !== false;
  const totals = useMemo(() => segmentTotals(rows), [rows]);
  const recordCount = Number(table.row_count || 0);
  // Completeness excludes the source-owned gap (fields we don't enrich) from the denom.
  const completePct = completeness(totals);

  return (
    <li className="border-b border-gray-100 last:border-0">
      {/* Header row + a full-width bar beneath it. The header wraps its controls when
          space runs out; the bar is never squeezed into a fixed grid column, which is
          what pushed the value sub-label under the bar at tablet widths. */}
      {/* Genuinely responsive: on a wide screen (xl) the bar sits inline between the
          label and the badges — the layout the desktop had. When the row runs out of
          width, flex-wrap drops the bar to its own full-width line (order-last +
          basis-full) so its labels never get crushed. One layout that adapts, not one
          that is always stacked. */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full text-left py-3 px-1 rounded-lg hover:bg-gray-50/70 flex flex-wrap items-center gap-x-3 gap-y-2 xl:flex-nowrap"
      >
        <span className="order-1 flex items-center gap-2 min-w-0 flex-1 basis-40 xl:flex-none xl:basis-52">
          <Chevron open={open} />
          <span className="min-w-0">
            <span className="block text-[15px] font-semibold text-gray-900">
              {entityLabel(table.table_name)}
            </span>
            {/* Spelling out records × fields = values stops a bar summing to 95,207
                from looking broken next to a "13,601 records" label. */}
            <span className="block text-xs text-gray-500 tabular-nums">
              {fmt(recordCount)} × {totals.fieldCount} fields = {fmt(totals.checked)} values
            </span>
          </span>
        </span>

        <span className="order-last basis-full min-w-0 xl:order-2 xl:basis-0 xl:flex-1">
          <CompositionBar totals={totals} />
        </span>

        {/* Health + completeness travel together, so on a narrow row they wrap as one
            unit rather than shoving each other off-screen.

            A non-enrichable entity gets NEITHER. "% complete" is measured against
            what we can fill, which for these is nothing — so it computes to a
            triumphant 100% that means the opposite of what it reads, and a health
            badge grades enrichment that never ran. What is true and useful is how
            much of it the ATS itself filled, so that is what the row says. */}
        <span className="order-2 xl:order-3 flex items-center gap-3 ml-auto xl:ml-0 flex-none">
          {enrichable ? (
            <>
              <DataQualityStatusBadge status={healthOf(totals)} />
              <span className="text-right whitespace-nowrap">
                <span className="block text-sm text-gray-700 tabular-nums">{pct(completePct)} complete</span>
                {totals.added > 0 && (
                  <span className="block text-xs tabular-nums" style={{ color: '#0f7d57' }}>
                    +{fmt(totals.added)} by SignalCue
                  </span>
                )}
              </span>
            </>
          ) : (
            <span className="text-right whitespace-nowrap">
              <span className="block text-sm text-gray-700 tabular-nums">
                {pct(pctOf(totals.filled, totals.checked))} from your ATS
              </span>
              <span className="block text-xs text-gray-400">not enriched</span>
            </span>
          )}
        </span>
      </button>
      {open && (
        <FieldRows
          rows={rows}
          loading={detailLoading}
          onViewData={onViewData}
          tableName={table.table_name}
          recordCount={recordCount}
        />
      )}
    </li>
  );
}

export default function QualityOverview({ overview, tables, coverage, runId, loading, onViewData, belowStats = null }) {
  // Per-field fill rates live behind /dq/tables/{t}. The profile is baked onto the run,
  // so each is a single-row lookup (~5ms) — fetch all three up front rather than making
  // the composition bar wait for a click.
  const [details, setDetails] = useState({});
  const [detailLoading, setDetailLoading] = useState(false);

  // enrichable comes from the backend (TableProfile.Enrichable). Older snapshots
  // predate the flag, so treat a missing value as enrichable: that reproduces the
  // previous single-list behaviour rather than silently demoting every entity.
  const enrichableTables = useMemo(
    () => (tables || []).filter((t) => t.enrichable !== false),
    [tables]
  );
  const sourceOnlyTables = useMemo(
    () => (tables || []).filter((t) => t.enrichable === false),
    [tables]
  );
  const tableNames = useMemo(() => (tables || []).map((t) => t.table_name).join(','), [tables]);

  useEffect(() => {
    if (!runId || !tableNames) return undefined;
    // Leaving the tab mid-fetch must not set state on an unmounted component.
    let alive = true;
    const names = tableNames.split(',');
    setDetailLoading(true);
    // Promise.resolve() so a service that throws synchronously degrades to an empty
    // field list rather than taking the page down with it.
    Promise.all(
      names.map((n) =>
        Promise.resolve()
          .then(() => dataQualityService.getTableDetail(runId, n))
          .catch(() => ({ comparisons: [] }))
      )
    ).then((got) => {
      if (!alive) return;
      setDetails(Object.fromEntries(names.map((n, i) => [n, got[i]?.comparisons || []])));
      setDetailLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [runId, tableNames]);

  const rowsByEntity = useMemo(() => {
    const out = {};
    (tables || []).forEach((t) => {
      // t.enrichable (backend: coverage.IsEnrichableEntity) makes a non-enrichable
      // entity wholly source-owned, so its gap renders as "Not enriched" instead of
      // a fillable Missing band. Older payloads omit the flag; undefined !== false,
      // so they keep the previous per-field behaviour.
      out[t.table_name] = fieldRows(
        t.table_name,
        details[t.table_name] || [],
        coverage,
        Number(t.row_count || 0),
        t.enrichable !== false
      );
    });
    return out;
  }, [tables, details, coverage]);

  const grand = useMemo(() => segmentTotals(Object.values(rowsByEntity).flat()), [rowsByEntity]);

  const totals = entityTotals(coverage);
  const records = overview?.total_rows ?? totals.companies + totals.people + totals.jobs;
  const addedShare = grand.filled ? Math.round((100 * grand.added) / grand.filled) : 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card label="Records" value={fmt(records)} hint="across every record type we profile" />
        <Card
          label="From your ATS"
          value={fmt(grand.source)}
          hint="values the export already carried"
          accent={PROV.source.c}
        />
        <Card
          label="Added by SignalCue"
          value={fmt(grand.added)}
          hint={`${addedShare}% of every value we hold`}
          accent={PROV.added.c}
        />
        <Card
          label="Missing"
          value={fmt(grand.missing - grand.source_owned)}
          hint={
            <>
              {fmt(grand.actionable)} can still be filled
              {grand.exhausted > 0 && (
                <>
                  <br />
                  {`${fmt(grand.exhausted)} tried, can't enrich`}
                </>
              )}
              {grand.source_owned > 0 && (
                <>
                  <br />
                  {`${fmt(grand.source_owned)} not enriched (from ATS)`}
                </>
              )}
            </>
          }
          loading={loading}
        />
      </div>

      {belowStats}

      <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">How complete is your data?</h3>
            <p className="text-sm text-gray-500 mt-1">
              Every record type, and where each value came from. Bars count individual{' '}
              <strong className="font-medium">values</strong> — one per record, per field — not records.
            </p>
          </div>
          <div className="flex flex-wrap gap-4 text-xs text-gray-600">
            {BAR_SEGMENTS.map((s) => (
              <span key={s} className="inline-flex items-center gap-1.5" title={PROV[s].hint}>
                <span
                  className="inline-block w-2.5 h-2.5 rounded-sm flex-none"
                  style={{ background: PROV[s].c, border: s === 'missing' ? '1px solid #c3c2b7' : 'none' }}
                />
                {PROV[s].label}
              </span>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="mt-5 space-y-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-12 bg-gray-100 rounded animate-pulse motion-reduce:animate-none" />
            ))}
          </div>
        ) : !tables?.length ? (
          <p className="mt-5 text-sm text-gray-500">Quality appears after your next import.</p>
        ) : (
          <>
            {/* Enrichable entities first: the ones whose gaps SignalCue offers to
                fill. Everything below the divider is the ATS's to fill — showing
                them together implied a promise we cannot keep for a call log or a
                placement fee. The split is driven by the backend's `enrichable`
                flag (coverage.IsEnrichableEntity), not a list held here, so an
                entity gains an enricher and moves up on its own. */}
            <ul className="mt-4">
              {enrichableTables.map((t) => (
                <EntityRow
                  key={t.table_name}
                  table={t}
                  rows={rowsByEntity[t.table_name] || []}
                  detailLoading={detailLoading}
                  onViewData={onViewData}
                />
              ))}
            </ul>

            {sourceOnlyTables.length > 0 && (
              <>
                <div className="mt-7 pt-5 border-t border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-900">Straight from your ATS</h4>
                  <p className="text-xs text-gray-500 mt-1">
                    SignalCue doesn&apos;t add to these. A call log, a CV entry, an agreed fee — those exist only
                    in your system, so there&apos;s nothing to look up.
                  </p>
                </div>
                <ul className="mt-3">
                  {sourceOnlyTables.map((t) => (
                    <EntityRow
                      key={t.table_name}
                      table={t}
                      rows={rowsByEntity[t.table_name] || []}
                      detailLoading={detailLoading}
                      onViewData={onViewData}
                    />
                  ))}
                </ul>
              </>
            )}
          </>
        )}

        <p className="text-xs text-gray-400 mt-5">
          A value counts as complete when it has a usable value. We don&apos;t check formats yet, so nothing is
          flagged invalid — which is why health is rated on completeness alone.
        </p>
      </section>
    </div>
  );
}
